// Deadlines — PI Platform outline v2 §3.11.
//
// The cross-matter view: what is due, everywhere, soonest first.
//
// One ordering rule drives the whole page. Past-due leads, always,
// regardless of the window filter. A date that has already gone is the
// most urgent thing the firm has, and sorting it below next week's
// would be a design choice with consequences — so it sits at the top
// and says how many days ago.
//
// Two things get called out rather than left to be noticed:
//
//   Unowned jurisdictional deadlines. A statute nobody is responsible
//   for is the specific thing that goes unnoticed until it is too late,
//   so it is counted in the summary and labelled on the row.
//
//   Why each date exists. The basis ("Alabama statute computed from
//   date of incident 2026-03-14") is shown, because a date whose origin
//   nobody can explain is a date nobody will trust enough to act on.
//
// No severity colours. Urgency is carried by position and by a plain
// count of days — nobody has defined what amber versus red would mean
// on a statute, and inventing a scale here would be worse than words.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

const KIND_LABELS = {
  sol: "Statute of limitations",
  notice_of_claim: "Notice of claim",
  discovery_response: "Discovery response",
  court_ordered: "Court ordered",
  statutory: "Statutory",
  contractual: "Contractual",
  internal: "Internal target",
  other: "Other",
};

const WINDOWS = [
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "1 year" },
  { days: 3650, label: "Everything" },
];

function whenText(d) {
  const n = d.days_remaining;
  if (n === null || n === undefined) return "";
  if (n < 0) return `${Math.abs(n)} day${Math.abs(n) === 1 ? "" : "s"} overdue`;
  if (n === 0) return "Due today";
  if (n === 1) return "Due tomorrow";
  return `${n} days`;
}

export function DeadlinesPage({ onMatterClick }) {
  const [data, setData] = useState(null);
  const [within, setWithin] = useState(90);
  const [error, setError] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});

  const load = useCallback(async () => {
    try {
      setData(await apiFetch(`/api/deadlines/upcoming?within_days=${within}`));
      setError(null);
    } catch (e) {
      setError(e.message);
      setData({ data: [], total: 0 });
    }
  }, [within]);

  useEffect(() => { load(); }, [load]);

  const act = async (path, opts) => {
    try {
      await apiFetch(path, opts);
      await load();
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const dismiss = async (d) => {
    const note = (noteDraft[d.id] || "").trim();
    if (!note) {
      setError(
        `Dismissing "${d.title}" needs a reason — it is the only record of `
        + "why the firm decided it did not apply.");
      return;
    }
    await act(`/api/deadlines/${d.id}/not-applicable`, {
      method: "POST", body: JSON.stringify({ note }),
    });
    setNoteDraft((p) => ({ ...p, [d.id]: "" }));
  };

  if (data === null) {
    return React.createElement("div", {
      style: { display: "flex", gap: "8px", alignItems: "center", padding: "40px" },
    }, React.createElement(Spinner, { size: 16 }),
       React.createElement("span", { style: { fontSize: "13px", color: C.textMuted } },
         "Loading deadlines…"));
  }

  const rows = data.data || [];

  return React.createElement("div", { style: { padding: "32px 40px", maxWidth: "940px" } },
    React.createElement("h1", {
      style: { fontFamily: "'Playfair Display', serif", fontSize: "32px",
               color: C.navy, marginBottom: "6px" },
    }, "Deadlines"),
    React.createElement("p", {
      style: { fontSize: "14px", color: C.textMuted, marginBottom: "20px" },
    }, "Every open deadline across every matter. Anything overdue is listed "
     + "first, whatever window you pick."),

    error && React.createElement("div", {
      style: { fontSize: "13px", color: C.textDark, background: C.warmWhite,
               border: `1px solid ${C.warmGray}`, borderRadius: "8px",
               padding: "11px 14px", marginBottom: "16px" },
    }, error),

    // Summary — the two numbers worth knowing at a glance.
    React.createElement("div", {
      style: { display: "flex", gap: "26px", flexWrap: "wrap", padding: "14px 18px",
               background: "#fff", border: `1px solid ${C.warmGray}`,
               borderRadius: "12px", marginBottom: "18px", alignItems: "center" },
    },
      _stat("Overdue", data.past_due ?? 0, "already past their date"),
      _stat("Jurisdictional", data.jurisdictional ?? 0, "statutory or court-set"),
      _stat("Unowned", data.unowned ?? 0, "nobody responsible"),
      React.createElement("div", { style: { flex: 1 } }),
      React.createElement("div", { style: { display: "flex", gap: "6px", flexWrap: "wrap" } },
        ...WINDOWS.map((w) => React.createElement("button", {
          key: w.days,
          onClick: () => setWithin(w.days),
          style: within === w.days ? primaryBtn : quietBtn,
        }, w.label)),
      ),
    ),

    rows.length === 0
      ? React.createElement("div", { style: cardStyle },
          React.createElement("p", {
            style: { fontSize: "13px", color: C.textMuted, margin: 0 },
          }, data.total === 0 && within >= 3650
              ? "No open deadlines on any matter. If that seems wrong, the "
                + "statutory dates are computed per matter from the state and "
                + "date of incident — check a matter's Deadlines card."
              : `Nothing due in the next ${within} days.`))
      : React.createElement("div", { style: { ...cardStyle, padding: "6px 24px 14px" } },
          ...rows.map((d) => React.createElement("div", {
            key: d.id,
            style: { borderTop: `1px solid ${C.warmGray}`, padding: "14px 0" },
          },
            React.createElement("div", {
              style: { display: "flex", gap: "10px", alignItems: "baseline", flexWrap: "wrap" },
            },
              React.createElement("span", {
                style: { fontSize: "14px", fontWeight: 700, color: C.navy,
                         minWidth: "120px" },
              }, whenText(d)),
              React.createElement("span", {
                style: { fontSize: "14px", fontWeight: 600, color: C.textDark },
              }, d.title),
              d.is_jurisdictional && React.createElement("span", { style: tagStyle },
                KIND_LABELS[d.kind] || d.kind),
              d.is_unowned && React.createElement("span", { style: tagStyle }, "Unassigned"),
              React.createElement("div", { style: { flex: 1 } }),
              React.createElement("span", {
                style: { fontSize: "12px", color: C.textMuted },
              }, d.due_date),
            ),

            React.createElement("div", {
              style: { fontSize: "12px", color: C.textMuted, marginTop: "3px" },
            },
              onMatterClick
                ? React.createElement("button", {
                    onClick: () => onMatterClick({ id: d.matter_id }),
                    style: { background: "none", border: "none", padding: 0,
                             color: C.navy, cursor: "pointer", fontSize: "12px",
                             textDecoration: "underline" },
                  }, `Matter ${d.matter_id}`)
                : `Matter ${d.matter_id}`,
              d.responsible_user ? ` · ${d.responsible_user}` : "",
              d.basis ? ` · ${d.basis}` : "",
            ),

            React.createElement("div", {
              style: { display: "flex", gap: "8px", marginTop: "9px",
                       flexWrap: "wrap", alignItems: "center" },
            },
              React.createElement("button", {
                onClick: () => act(`/api/deadlines/${d.id}/complete`,
                  { method: "POST", body: JSON.stringify({}) }),
                style: primaryBtn,
              }, "Mark done"),
              React.createElement("input", {
                value: noteDraft[d.id] || "",
                onChange: (e) => setNoteDraft((p) => ({ ...p, [d.id]: e.target.value })),
                placeholder: "Reason, if this does not apply",
                style: { ...inputStyle, flex: "1 1 240px" },
              }),
              React.createElement("button", {
                onClick: () => dismiss(d), style: quietBtn,
              }, "Does not apply"),
            ),
          )),
        ),
  );
}

function _stat(label, value, hint) {
  return React.createElement("div", { key: label, style: { minWidth: "96px" } },
    React.createElement("div", {
      style: { fontSize: "10px", color: "#9A948C", fontWeight: 700,
               textTransform: "uppercase", letterSpacing: "0.06em" },
    }, label),
    React.createElement("div", {
      style: { fontSize: "20px", fontWeight: 700, color: "#1B2A4A", marginTop: "2px" },
    }, value),
    React.createElement("div", {
      style: { fontSize: "10px", color: "#9A948C", marginTop: "1px" },
    }, hint));
}

const cardStyle = {
  background: "#fff", borderRadius: "12px", padding: "20px 24px",
  border: "1px solid #E8E3DC", marginBottom: "20px",
};
const inputStyle = {
  padding: "7px 10px", borderRadius: "6px", border: "1px solid #E8E3DC",
  fontSize: "12px", fontFamily: "inherit", background: "#fff",
  boxSizing: "border-box", minWidth: 0,
};
const quietBtn = {
  border: "1px solid #E8E3DC", background: "#fff", borderRadius: "6px",
  padding: "7px 13px", fontSize: "12px", fontWeight: 600,
  color: "#1B2A4A", cursor: "pointer", flexShrink: 0,
};
const primaryBtn = {
  border: "1px solid #1B2A4A", background: "#1B2A4A", borderRadius: "6px",
  padding: "7px 14px", fontSize: "12px", fontWeight: 600,
  color: "#fff", cursor: "pointer", flexShrink: 0,
};
const tagStyle = {
  fontSize: "10px", fontWeight: 700, textTransform: "uppercase",
  letterSpacing: "0.06em", color: "#6B6660",
  border: "1px solid #E8E3DC", borderRadius: "4px", padding: "2px 7px",
};
